import { createContext, useContext } from 'react'

/**
 * Superfície de jogo — a **única** dependência de execução que um tipo de
 * desafio pode ter (D4c).
 *
 * Deliberadamente estreita e sem registro de domínio: um kind que consome só
 * daqui não tem como descobrir se está numa jornada ou num quiz. É isso que
 * torna possível uma árvore única de kinds em vez de duas em espelho.
 *
 * A lista abaixo não foi desenhada, foi **medida**: é exatamente o que os kinds
 * dos dois domínios pedem hoje aos seus contextos. Nenhum deles pede `level`,
 * `quiz`, `journey` ou `config` — quem precisa disso é a camada de shell, que
 * continua com contexto de domínio próprio.
 *
 * Crescer esta interface é um sinal de alerta: se um kind precisa de algo que
 * não está aqui, pergunte primeiro se ele não deveria ser uma tela de domínio.
 * Foi o caso do `Initial`, que exige `level`/`config` e por isso **não** é kind.
 */
export interface LevelPlaySurface {
  /** Conclui o passo com a resposta dada, sem passar por verificação. */
  onComplete: (data: { answer: unknown }) => void
  /** Finaliza e avança — usado por slides e pelo modo de interação "continue". */
  onContinue: (data: { uid: string; answer?: unknown }) => Promise<void>
  /** Submete uma resposta para verificação. */
  onAnswer: (data: {
    uid: string
    answer?: unknown
    answerIsCorrect?: boolean
    skipValidation?: boolean
    voucherId?: number
  }) => Promise<void>
  /** Desconta uma vida — jogos que penalizam erro dentro do próprio tabuleiro. */
  loseHeart: () => void
  /** Permite ao kind injetar seu próprio footer no step. */
  setStepsCustomFooters: (stepId: string, customFooter: React.ReactNode) => void

  isSubmitting: boolean
  disableInputs: boolean
  answerIsWrong: boolean
  answerIsRight?: boolean
}

const LevelPlaySurfaceContext = createContext<LevelPlaySurface | null>(null)

/**
 * Consumido pelos tipos de desafio. Lança se usado fora do provider — um kind
 * montado sem superfície é erro de composição, não estado válido.
 */
export const useLevelPlaySurface = (): LevelPlaySurface => {
  const surface = useContext(LevelPlaySurfaceContext)

  if (!surface) {
    throw new Error(
      'useLevelPlaySurface precisa estar dentro de <LevelPlaySurfaceProvider>'
    )
  }

  return surface
}

/**
 * Montado pelo provider de cada domínio, alimentado pelo motor.
 *
 * O domínio continua tendo seu próprio contexto para o registro do recurso —
 * os dois convivem, com responsabilidades separadas.
 */
export const LevelPlaySurfaceProvider = ({
  surface,
  children
}: {
  surface: LevelPlaySurface
  children: React.ReactNode
}) => (
  <LevelPlaySurfaceContext.Provider value={surface}>
    {children}
  </LevelPlaySurfaceContext.Provider>
)
